import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Flex, Modal, Space, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../../../Components/Button"
import EmptyComponent from "../../../../Components/Empty"
import Filters from "../../../../Components/Filters"
import Table from "../../../../Components/Table"
import Icon from "../../../../Icon"
import { modalConfig } from "../../../../theme"
import { usePricingTabPreferences } from "../../hooks/usePricingTabPreferences"
import { PRICING_TAB } from "../../redux/pricing.constants"
import {
  changeFilters,
  changeVisibleFilters,
  resetFilters,
  selectArePricingFiltersDefault,
  selectLookupPricing,
  selectLookupPricingFetching,
  selectPricingFilterOptions,
  selectPricingFilters,
  selectPricingSaveFetching,
  selectPricingVisibleFilters
} from "../../redux/pricing.slice"
import {
  createLookupPricing,
  deleteLookupPricing,
  getLookupPricing,
  getLookupPricingFilterOptions,
  updateLookupPricing
} from "../../redux/pricing.thunks"
import { serializeLookupFilters } from "../../utils/serializeLookupFilters"
import { getLookupColumns } from "./columns"
import LookupPricingForm from "./components/LookupPricingForm"

const TAB = PRICING_TAB.LOOKUPS

const DEFAULT_PAGE_SIZE = 50

const { Text } = Typography

const allFilters = [
  { field_id: "pricing_schema", label: "Pricing scheme", active: true },
  { field_id: "free_lookups", label: "Free lookups", active: true },
  { field_id: "applied", label: "Applied", active: true }
]

const LookupsTab = () => {
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()
  const dispatch = useDispatch()

  const isMobile = useSelector((state) => state.nav.isMobile)
  const { count, results } = useSelector(selectLookupPricing)
  const fetching = useSelector(selectLookupPricingFetching)
  const filters = useSelector(selectPricingFilters(TAB))
  const visibleFilters = useSelector(selectPricingVisibleFilters(TAB))
  const filterOptions = useSelector(selectPricingFilterOptions(TAB))
  const isFiltersDefault = useSelector(selectArePricingFiltersDefault(TAB))
  const saveFetching = useSelector(selectPricingSaveFetching)

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [ordering, setOrdering] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedScheme, setSelectedScheme] = useState(null)

  const fetchList = () => {
    dispatch(
      getLookupPricing({
        page: pageNumber,
        page_size: pageSize,
        ...(ordering && { ordering }),
        ...serializeLookupFilters(filters)
      })
    )
  }

  usePricingTabPreferences(TAB)

  useEffect(() => {
    dispatch(getLookupPricingFilterOptions())
  }, [])

  useEffect(() => {
    fetchList()
  }, [ordering, pageNumber, pageSize, filters])

  const handleCloseModal = () => {
    setSelectedScheme(null)
    setIsModalOpen(false)
  }

  const notifySaved = (message) => {
    notification.success({
      message,
      closeIcon: <Text>{t("Dismiss")}</Text>,
      style: { minWidth: "max-content", width: 475 }
    })
    handleCloseModal()
  }

  // An update patches its row in place; create and delete change the list and the numbering, so
  // they refetch. On a failure the thunk has already shown the API error and the modal stays open.
  const createScheme = async (data) => {
    try {
      await dispatch(createLookupPricing(data)).unwrap()
      notifySaved("New pricing scheme has been successfully created.")
      fetchList()
    } catch {}
  }

  const updateScheme = async (data) => {
    try {
      await dispatch(updateLookupPricing({ id: selectedScheme.id, ...data })).unwrap()
      notifySaved("Pricing scheme has been successfully updated.")
    } catch {}
  }

  const deleteScheme = async (id) => {
    try {
      await dispatch(deleteLookupPricing(id)).unwrap()
      fetchList()
    } catch {}
  }

  const handleChangeTable = useCallback((pagination, _, sorter) => {
    let nextOrdering = null

    if (sorter?.order) {
      nextOrdering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
    }

    setPageNumber(pagination.current)
    setPageSize(pagination.pageSize)
    setOrdering(nextOrdering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      dispatch(changeVisibleFilters({ tab: TAB, visibleFilters: newData }))
    } else {
      dispatch(changeFilters({ tab: TAB, filters: newData }))
      setPageNumber(1)
    }
  }, [])

  const handleClearFilters = () => {
    dispatch(resetFilters(TAB))
    setPageNumber(1)
  }

  const handleSelectRow = (scheme, type) => {
    if (type === "edit") {
      setSelectedScheme(scheme)
      setIsModalOpen(true)
      return
    }

    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,
      title: "Delete pricing scheme",
      content:
        "Are you sure you want to delete this pricing scheme? This action can NOT be undone and all business accounts using it will be switched to Default pricing scheme automatically.",
      cancelText: t("Cancel"),
      okText: t("Delete"),
      okButtonProps: { ...modalConfig("exclamation")?.okButtonProps, danger: true, ghost: true },
      onOk: () => deleteScheme(scheme.id)
    })
  }

  const handleSubmitForm = (data) => {
    if (!selectedScheme) {
      createScheme(data)
      return
    }

    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,
      title: "Saving prices",
      content:
        "Are you sure you want to save the changes? The new prices will apply to lookups received from now on by all business accounts using the current pricing scheme. Lookups already charged keep their amount.",
      cancelText: t("Cancel"),
      okText: t("Save"),
      onOk: () => updateScheme(data)
    })
  }

  const columns = useMemo(() => getLookupColumns({ onSelectRow: handleSelectRow }), [])

  const formInitialValues = selectedScheme && {
    name: selectedScheme.name,
    free_count: selectedScheme.free_count,
    banking_app_price: selectedScheme.banking_app_price,
    call_center_price: selectedScheme.call_center_price,
    mastercom_price: selectedScheme.mastercom_price
  }

  return (
    <>
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={allFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          withoutAdd={true}
          showClearBtn={!isFiltersDefault}
          onClearFilters={handleClearFilters}
        />

        <Space style={{ flexShrink: 0 }}>
          <Button
            className="flex center"
            icon={<Icon type="plus" style={{ fontSize: 20 }} />}
            onClick={() => setIsModalOpen(true)}
          >
            New pricing scheme
          </Button>
        </Space>
      </Flex>

      <Table
        rowKey="id"
        columns={columns}
        loading={fetching}
        dataSource={results}
        scroll={{
          x: "max-content",
          y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 56px)",
          scrollToFirstRowOnChange: true
        }}
        onChange={handleChangeTable}
        pagination={{ total: count, current: pageNumber, pageSize }}
        showSizeChanger={true}
        locale={{
          emptyText: isFiltersDefault ? (
            <EmptyComponent type="no_data_available" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type={"menu_search"} />}
                onClick={handleClearFilters}
              >
                {t("Clear search")}
              </Button>
            </EmptyComponent>
          )
        }}
      />

      <Modal
        open={isModalOpen}
        centered={!isMobile}
        width={442}
        title={"Pricing scheme"}
        footer={null}
        closable="true"
        onCancel={handleCloseModal}
        destroyOnHidden
        classNames={{ content: "clear-bg" }}
      >
        <Spin size="large" spinning={saveFetching}>
          <LookupPricingForm
            pricingNames={filterOptions.pricing_schema}
            initialValues={formInitialValues}
            isDefault={!!selectedScheme?.is_default}
            onSubmit={handleSubmitForm}
            onClose={handleCloseModal}
          />
        </Spin>
      </Modal>
    </>
  )
}

export default LookupsTab
